import {
  ChangeEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDebounce from 'react-use/lib/useDebounce';

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  Value,
} from '@material-ui/lab';

import isArray from 'lodash/isArray';
import isString from 'lodash/isString';

import { useLocalStorage } from '@spotify/confidence-core-react';

import { useIdentityAutocompleteConfig } from '../../extensions';
import {
  useCurrentUser,
  useCurrentUserGroups,
  useIdentitySearch,
} from '../../hooks';
import { IdentityKind, identityKindToFilter } from '../../utils';
import { IdentityChip } from '../IdentityChip';
import { IdentityItem } from '../IdentityItem';

// Helper type for autocomplete value based on multiple and disableClearable props
type IdentityAutocompleteValue<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
> = Multiple extends true
  ? string[]
  : DisableClearable extends true
    ? string
    : string | null;

export type IdentityAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
> = Omit<
  TextFieldProps,
  'onChange' | 'value' | 'options' | 'renderOption' | 'renderTags' | 'error'
> & {
  value?: IdentityAutocompleteValue<Multiple, DisableClearable>;
  onChange?: (
    event: ChangeEvent<{}>,
    value: IdentityAutocompleteValue<Multiple, DisableClearable>,
    reason: string,
    details?: AutocompleteChangeDetails<string>,
  ) => void;
  multiple?: Multiple;
  disableClearable?: DisableClearable;
  disabledOptions?: string[];
  loading?: boolean;
  error?: boolean | string;
  allowedKinds?: readonly IdentityKind[];
};

export function IdentityAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  disabledOptions = [],
  value,
  onChange,
  multiple,
  disableClearable,
  loading: externalLoading,
  disabled,
  label,
  placeholder,
  required,
  variant = 'outlined',
  margin = 'dense',
  helperText,
  error,
  allowedKinds,
  ...rest
}: IdentityAutocompleteProps<Multiple, DisableClearable>) {
  const { showUserGroupsFilter } = useIdentityAutocompleteConfig();
  const { groups: userGroups, isLoading: userGroupsLoading } =
    useCurrentUserGroups();
  const { currentUser } = useCurrentUser();

  const [showOnlyUserGroups, setShowOnlyUserGroups] = useLocalStorage(
    'identity-autocomplete-show-only-user-groups',
    true,
  );

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useDebounce(
    () => {
      setDebouncedQuery(searchQuery);
    },
    300,
    [searchQuery],
  );

  const userGroupsNames = useMemo(() => {
    const allUserGroupsNames = userGroups.map(group =>
      'name' in group.identity ? group.identity.name : group.name,
    );
    if (currentUser?.identity?.name) {
      allUserGroupsNames.push(currentUser.identity.name);
    }
    return allUserGroupsNames;
  }, [currentUser, userGroups]);

  const userGroupsFilter = useMemo(() => {
    if (
      showUserGroupsFilter &&
      showOnlyUserGroups &&
      userGroupsNames.length > 0
    ) {
      return `name:(${userGroupsNames.map(name => `"${name}"`).join(' OR ')})`;
    }
    return undefined;
  }, [showUserGroupsFilter, showOnlyUserGroups, userGroupsNames]);

  // Combine filters (userGroupsFilter only for now)
  const baseFilter = userGroupsFilter;

  const identityKinds = allowedKinds?.map(k => identityKindToFilter[k]);

  const { identities, isLoading, hasNextPage, nextPage, isLoadingNextPage } =
    useIdentitySearch({
      query: debouncedQuery || '*',
      identityKinds,
      filter: baseFilter,
      skip: disabled,
    });

  // Always fetch selected values to ensure they're visible in the dropdown
  const selectedValueFilter = useMemo(() => {
    const selectedNames: string[] = [];
    if (multiple && Array.isArray(value)) {
      selectedNames.push(...value.filter(Boolean));
    } else if (value && typeof value === 'string') {
      selectedNames.push(value);
    }

    if (selectedNames.length === 0) return undefined;

    return `name:(${selectedNames.map(name => `"${name}"`).join(' OR ')})`;
  }, [value, multiple]);

  // Fetch selected values in parallel to ensure they're visible when dropdown opens
  const { identities: selectedIdentities } = useIdentitySearch({
    query: '*',
    identityKinds,
    filter: selectedValueFilter,
    skip: disabled || !selectedValueFilter,
  });

  // Combine identities from both queries
  const allIdentities = useMemo(() => {
    const combined = [...identities];
    // Only prioritize selected identities when not actively searching
    // When searching, let search results determine order
    if (!debouncedQuery || debouncedQuery === '*') {
      selectedIdentities.forEach(identity => {
        // Remove from current position if already present
        const existingIndex = combined.findIndex(i => i.name === identity.name);
        if (existingIndex !== -1) {
          combined.splice(existingIndex, 1);
        }
        // Add to beginning when not searching
        combined.unshift(identity);
      });
    }
    return combined;
  }, [identities, selectedIdentities, debouncedQuery]);

  // Sync inputValue with selected value's displayName (only when not actively searching)
  useEffect(() => {
    // Don't interfere with user's typing - only sync when not searching
    if (searchQuery) return;

    if (!multiple && value && typeof value === 'string') {
      const identity = allIdentities.find(i => i.name === value);
      if (identity?.displayName) {
        setInputValue(identity.displayName);
      }
    } else if (!multiple && !value) {
      setInputValue('');
    }
  }, [value, allIdentities, multiple, searchQuery, setInputValue]);

  // Clear selected value when the "Only show groups I belong to" checkbox is
  // toggled ON, but not on initial mount or when groups finish loading.
  const prevShowOnlyUserGroupsRef = useRef(showOnlyUserGroups);

  useEffect(() => {
    const wasOff = prevShowOnlyUserGroupsRef.current === false;
    prevShowOnlyUserGroupsRef.current = showOnlyUserGroups;

    // Only clear when the user actively enables the filter
    if (!wasOff || !showOnlyUserGroups) return;

    if (showUserGroupsFilter && value) {
      const selectedArray = isArray(value) ? value : [value];
      const hasInvalidSelection = selectedArray.some(
        s => s && !userGroupsNames.includes(s),
      );

      if (hasInvalidSelection) {
        onChange?.(
          {} as ChangeEvent<{}>,
          (multiple ? [] : null) as IdentityAutocompleteValue<
            Multiple,
            DisableClearable
          >,
          'clear',
        );
      }
    }
  }, [
    showUserGroupsFilter,
    showOnlyUserGroups,
    userGroupsNames,
    value,
    onChange,
    multiple,
  ]);

  // Ensure current selection is valid for the active filter
  const sanitizedValue = useMemo(() => {
    const allowedNames =
      showUserGroupsFilter && showOnlyUserGroups
        ? new Set(userGroupsNames)
        : undefined;

    if (multiple) {
      const arrayValue = isArray(value) ? value : [];
      if (!allowedNames) {
        return arrayValue as IdentityAutocompleteValue<
          Multiple,
          DisableClearable
        >;
      }
      const filteredArray = arrayValue.filter(v => v && allowedNames.has(v));
      return filteredArray as IdentityAutocompleteValue<
        Multiple,
        DisableClearable
      >;
    }

    const singleValue = isString(value) ? value : null;
    if (!allowedNames) {
      return singleValue as IdentityAutocompleteValue<
        Multiple,
        DisableClearable
      >;
    }
    const valid =
      singleValue && allowedNames.has(singleValue) ? singleValue : null;
    return valid as IdentityAutocompleteValue<Multiple, DisableClearable>;
  }, [
    value,
    multiple,
    showUserGroupsFilter,
    showOnlyUserGroups,
    userGroupsNames,
  ]);

  // Build options list from identities
  const options = useMemo(() => {
    return allIdentities.map(identity => identity.name);
  }, [allIdentities]);

  // Build identity lookup map
  const identityMap = useMemo(() => {
    return new Map(allIdentities.map(identity => [identity.name, identity]));
  }, [allIdentities]);

  const loading = isLoading || userGroupsLoading || externalLoading;
  const isLoadingSearch = debouncedQuery !== searchQuery || isLoading;

  // Handle error prop - can be boolean or string
  const hasError = typeof error === 'string' ? error.length > 0 : !!error;
  const errorHelperText = typeof error === 'string' ? error : helperText;

  // Determine if there are no results after filtering
  const hasNoResults = allIdentities.length === 0 && !isLoading;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Autocomplete<string, Multiple, DisableClearable, false>
          options={options}
          value={
            sanitizedValue as Value<string, Multiple, DisableClearable, false>
          }
          onChange={(_event, newValue, reason) => {
            // Update inputValue and clear search query when selection is made
            if (reason === 'select-option') {
              if (multiple) {
                setInputValue('');
                setSearchQuery('');
              } else if (newValue) {
                const identity = allIdentities.find(i => i.name === newValue);
                setInputValue(identity?.displayName || '');
                setSearchQuery('');
              }
            } else if (reason === 'clear' || reason === 'remove-option') {
              setInputValue('');
              setSearchQuery('');
            }

            onChange?.(
              {} as ChangeEvent<{}>,
              newValue as IdentityAutocompleteValue<Multiple, DisableClearable>,
              reason as 'select-option' | 'remove-option' | 'clear',
            );
          }}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            // Update both inputValue and search query when user is typing
            if (reason === 'input') {
              setInputValue(newInputValue);
              setSearchQuery(newInputValue);
            } else if (reason === 'clear') {
              setInputValue('');
              setSearchQuery('');
            }
          }}
          filterOptions={x => x}
          disableListWrap
          openOnFocus
          autoHighlight
          multiple={multiple}
          disableClearable={disableClearable}
          disabled={disabled}
          loading={loading}
          getOptionDisabled={option => disabledOptions.some(d => d === option)}
          getOptionLabel={option => {
            const identity = identityMap.get(option);
            return identity?.displayName || option;
          }}
          renderOption={option => {
            const identity = identityMap.get(option);
            return identity ? <IdentityItem identity={identity} /> : option;
          }}
          renderTags={(tagOptions, getTagProps) =>
            tagOptions.map((option, index) => {
              const identity = identityMap.get(option);
              if (!identity) return null;
              return (
                <IdentityChip
                  key={option}
                  identityRef={identity}
                  {...getTagProps({ index })}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              {...rest}
              label={label}
              placeholder={placeholder}
              required={required}
              variant={variant}
              margin={margin}
              helperText={errorHelperText}
              error={hasError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingSearch && (
                      <CircularProgress size="1em" color="inherit" />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={hasNoResults ? 'No identities found' : 'No options'}
          ListboxProps={{
            style: { maxHeight: '300px' },
            onScroll: (event: SyntheticEvent) => {
              const listboxNode = event.currentTarget;
              const { scrollHeight, scrollTop, clientHeight } = listboxNode;

              const endIsVisible =
                scrollHeight - scrollTop - clientHeight <= 10;
              if (
                endIsVisible &&
                hasNextPage &&
                !isLoading &&
                !isLoadingNextPage
              ) {
                nextPage();
              }
            },
          }}
        />
      </Grid>
      {showUserGroupsFilter && (
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyUserGroups}
                onChange={e => setShowOnlyUserGroups(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Only show groups I belong to
              </Typography>
            }
          />
        </Grid>
      )}
    </Grid>
  );
}
